<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import {
  useStoreGetters,
  useMapGetter,
  useStore,
} from 'dashboard/composables/store';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import WhatsAppCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignDialog.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import WhatsAppCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/WhatsAppCampaignEmptyState.vue';
import CampaignStatsPanel from 'dashboard/components-next/Campaigns/CampaignStatsPanel.vue';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const selectedCampaign = ref(null);
const editingCampaign = ref(null);
const statsCampaign = ref(null);
const [showWhatsAppCampaignDialog, toggleWhatsAppCampaignDialog] = useToggle();
const [showStatsPanel, toggleStatsPanel] = useToggle();

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);

const confirmDeleteCampaignDialogRef = ref(null);

const WhatsAppCampaigns = computed(
  () => getters['campaigns/getWhatsAppCampaigns'].value
);

const hasNoWhatsAppCampaigns = computed(
  () => WhatsAppCampaigns.value?.length === 0 && !isFetchingCampaigns.value
);

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};

const handleView = campaign => {
  statsCampaign.value = null;
  toggleStatsPanel(false);
  editingCampaign.value = campaign;
  toggleWhatsAppCampaignDialog(true);
};

const handleStats = campaign => {
  editingCampaign.value = null;
  toggleWhatsAppCampaignDialog(false);
  statsCampaign.value = campaign;
  toggleStatsPanel(true);
};

const handleDialogClose = () => {
  editingCampaign.value = null;
  toggleWhatsAppCampaignDialog(false);
};

const handleRefresh = () => {
  store.dispatch('campaigns/get');
};
</script>

<template>
  <CampaignStatsPanel
    v-if="showStatsPanel && statsCampaign"
    :campaign="statsCampaign"
    @close="toggleStatsPanel(false)"
  />
  <CampaignLayout
    :header-title="t('CAMPAIGN.WHATSAPP.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN')"
    show-refresh
    @click="toggleWhatsAppCampaignDialog()"
    @close="handleDialogClose"
    @refresh="handleRefresh"
  >
    <template #action>
      <WhatsAppCampaignDialog
        v-if="showWhatsAppCampaignDialog"
        :campaign="editingCampaign"
        @close="handleDialogClose"
      />
    </template>
    <div
      v-if="isFetchingCampaigns"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <CampaignList
      v-else-if="!hasNoWhatsAppCampaigns"
      :campaigns="WhatsAppCampaigns"
      @delete="handleDelete"
      @view="handleView"
      @stats="handleStats"
    />
    <WhatsAppCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.TITLE')"
      :subtitle="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.SUBTITLE')"
      class="pt-14"
    />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
  </CampaignLayout>
</template>
